import React, { useState } from 'react';

/* ═══════════════════════════════════════════════════════════════
   User Guide – comprehensive step-by-step documentation
   ═══════════════════════════════════════════════════════════════ */

const SECTIONS = [
  {
    id: 'overview',
    title: 'System Overview',
    content: [
      { type: 'text', value: 'Agentfloww MRP is a full-featured Material Requirements Planning system designed for ARM Welders. It helps you manage inventory, plan production, handle purchasing, and run MRP calculations to keep your supply chain running smoothly.' },
      { type: 'heading', value: 'Key Modules' },
      { type: 'list', items: [
        'Dashboard — Real-time KPIs, quick-access cards, and system alerts',
        'Inventory — Item Master Data management (raw materials, components, finished goods)',
        'Sales — Sales Orders for managing customer demand',
        'Production — Production Order creation and tracking',
        'MRP — Forecasts, MRP Wizard, Order Recommendations, Company Calendar',
        'Purchasing — Purchase Orders and Vendor Management',
        'Inventory Transfers — Inventory Transfer Requests (ITR) and Inventory Transfers (IT) for warehouse-to-warehouse stock movement',
      ]},
      { type: 'heading', value: 'Navigation' },
      { type: 'list', items: [
        'Left Sidebar — Expand/collapse module categories to access all pages',
        'Top Menu Bar — File, Edit, View, Modules and Settings. Modules and Settings take you to pages; Settings is the only route to SAP Integration, Live Updates, Numbering and Email Notifications',
        'Tab Strip — Switch between Cockpit, MRP, Inventory, Purchasing and Production tabs',
        'View menu — Zoom In, Zoom Out and Reset Zoom enlarge the whole screen, not just the text, and your choice is remembered next time you sign in',
        'Settings → System Information — shows which version you are running, useful when reporting a problem',
      ]},
      { type: 'heading', value: 'On a phone or tablet' },
      { type: 'list', items: [
        'Tap the ☰ button at the top left to open the menu, and the ✕ or the greyed area to close it',
        'Choosing a page closes the menu for you',
        'Wide tables slide sideways with your finger instead of stretching the page',
      ]},
    ],
  },
  {
    id: 'signing-in',
    title: 'Signing In & Your Session',
    content: [
      { type: 'text', value: 'Every person has their own account. Sign in with your work email address and password. The system records who signed in and when, so a document always says who raised it.' },
      { type: 'heading', value: 'Signing in' },
      { type: 'steps', items: [
        'Open the site in your browser and enter your email address and password.',
        'If the password is wrong several times in a row the account locks itself. Ask a Super Admin to unlock it.',
        'If your account is inactive you will be told so rather than left guessing.',
      ]},
      { type: 'heading', value: 'You are signed out after 30 minutes of not using it' },
      { type: 'text', value: 'If you do not type, tap or scroll for 30 minutes, the system signs you out. This protects your work on a shared or unattended machine, and it means the record of who was working is accurate.' },
      { type: 'steps', items: [
        'One minute before it happens, a box appears asking whether you are still there.',
        'Choose "Stay signed in" to carry on. Your session is extended straight away.',
        'Choose "Sign out now", or do nothing, and you are returned to the sign-in screen.',
      ]},
      { type: 'tip', value: 'Moving the mouse, typing or scrolling all count as being there. The clock only runs while you are genuinely away, and simply leaving a page open does not keep you signed in.' },
      { type: 'note', value: 'Reloading the page does not reset the timer. If you were away for 30 minutes you will be asked to sign in again.' },
    ],
  },
  {
    id: 'dashboard',
    title: 'Dashboard (Cockpit)',
    content: [
      { type: 'text', value: 'The Dashboard is your home screen. It provides a quick overview of your entire MRP system at a glance.' },
      { type: 'heading', value: 'Step 1: View KPI Cards' },
      { type: 'text', value: 'At the top of the dashboard, KPI cards show:' },
      { type: 'list', items: [
        'Open Purchase Orders — Open PO count',
        'Items Below Min Stock — Items under minimum quantity',
        'Overdue Delivery Date — Planned MRP lines whose resolved delivery date (sales order date or required date) is before today',
        'Project pending — Distinct project codes with planned lines (latest MRP run)',
        'Pending production orders — Orders not Completed or Cancelled',
      ]},
      { type: 'heading', value: 'Step 2: Quick Access Cards' },
      { type: 'text', value: 'Click any quick-access card to navigate directly to that module:' },
      { type: 'list', items: [
        'Item Master — Opens the Item Master Data page',
        'Purchase Orders — Opens the Purchase Orders list',
        'MRP Wizard — Opens the MRP Wizard to run a new MRP calculation',
        'Forecasts — Opens the Forecast management page',
      ]},
      { type: 'heading', value: 'Step 3: Messages & Alerts' },
      { type: 'text', value: 'The alerts panel on the right shows system notifications such as:' },
      { type: 'list', items: [
        'Items below minimum stock levels',
        'Purchase orders past their delivery date',
        'Production orders that are overdue',
        'If everything is in order, you will see "All clear — no alerts"',
      ]},
    ],
  },
  {
    id: 'item-master',
    title: 'Item Master Data',
    content: [
      { type: 'text', value: 'The Item Master Data page lets you manage all items in your system — raw materials, components, and finished goods.' },
      { type: 'heading', value: 'Step 1: View Items' },
      { type: 'text', value: 'Navigate to Inventory → Item Master Data from the sidebar. You will see a table listing all items with columns: Item No., Description, Group, UOM, Unit Price, Available stock, and Status.' },
      { type: 'heading', value: 'Step 2: Search and Filter' },
      { type: 'text', value: 'Use the search bar at the top to search by item number or description. Use the group filter dropdown to filter items by category (Raw Material, Component, Finished Good).' },
      { type: 'heading', value: 'Step 3: Add a New Item' },
      { type: 'list', items: [
        'Click the "Add Item" button in the top right corner',
        'Fill in the required fields: Item No. (auto-generated), Description, Item Group',
        'Set the UOM (Unit of Measure), Unit Price, and Procurement Method (Buy or Make)',
        'Configure the Planning Method (MRP or None) and Lead Time Days',
        'Set Min Order Qty and Reorder Point for automatic reordering',
        'Choose the Preferred Vendor from the dropdown (for Buy items)',
        'Click "Save" to create the item',
      ]},
      { type: 'heading', value: 'Step 4: Edit an Existing Item' },
      { type: 'text', value: 'Click any row in the item table to open the Item Detail page. Here you can modify all fields and click "Update" to save changes.' },
      { type: 'heading', value: 'Step 5: Item Detail Tabs' },
      { type: 'text', value: 'The Item Detail page has multiple tabs:' },
      { type: 'list', items: [
        'General — Basic item information, pricing, procurement settings',
        'Inventory — Warehouse stock levels, available quantities',
        'Planning — MRP planning parameters, lead times, reorder points',
        'Bill of Materials — Component list for manufactured items (Make items)',
      ]},
      { type: 'tip', value: 'Items marked as "Make" can have a Bill of Materials (BOM) attached. Items marked as "Buy" are purchased from vendors.' },
    ],
  },
  {
    id: 'vendors',
    title: 'Vendor Management',
    content: [
      { type: 'text', value: 'The Vendor Management page lets you create and manage your supplier database.' },
      { type: 'heading', value: 'Step 1: View Vendors' },
      { type: 'text', value: 'Navigate to Purchasing → Vendors from the sidebar. You will see a table with: Vendor Code, Vendor Name, Contact Person, Payment Terms, Currency, and PO Count (number of purchase orders linked to this vendor).' },
      { type: 'heading', value: 'Step 2: Add a New Vendor' },
      { type: 'list', items: [
        'Click the "Add Vendor" button',
        'The Vendor Code is auto-generated (e.g., V-1006)',
        'Enter the Vendor Name (required)',
        'Fill in Contact Person name and Email',
        'Enter Phone number and Address',
        'Select Payment Terms from the dropdown (Net 15, Net 30, Net 45, Net 60, Net 90, COD, or Advance Payment)',
        'Choose the Currency (INR, USD, EUR, JPY, etc.)',
        'Click "Save" to create the vendor',
      ]},
      { type: 'heading', value: 'Step 3: Edit a Vendor' },
      { type: 'text', value: 'Click the pencil icon (✏️) on any vendor row to open the edit modal. Modify the fields and click "Update".' },
      { type: 'heading', value: 'Step 4: Delete a Vendor' },
      { type: 'text', value: 'Click the trash icon (🗑) on a vendor row. A confirmation dialog will appear. Note: You cannot delete a vendor that has existing purchase orders linked to it.' },
      { type: 'tip', value: 'Always set up your vendors before creating Purchase Orders. The vendor must exist in the system before it can be selected on a PO.' },
    ],
  },
  {
    id: 'purchase-orders',
    title: 'Purchase Orders',
    content: [
      { type: 'text', value: 'Purchase Orders (POs) are used to order materials from your vendors.' },
      { type: 'heading', value: 'Step 1: View Purchase Orders' },
      { type: 'text', value: 'Navigate to Purchasing → Purchase Order from the sidebar. The list shows: PO Number, Vendor, Posting Date, Delivery Date, Status, Currency, and Total Amount.' },
      { type: 'heading', value: 'Step 2: Create a New Purchase Order' },
      { type: 'list', items: [
        'Click "Add PO" to open the PO creation form',
        'Select a Vendor from the dropdown',
        'Set the Posting Date and Delivery Date',
        'Choose Currency and Payment Terms',
        'Add line items: select an Item, enter Quantity, Unit Price, and optionally set a Discount %',
        'The system auto-calculates line totals and the grand total',
        'Add any remarks or shipping instructions',
        'Click "Save" to create the PO in Draft status',
      ]},
      { type: 'heading', value: 'Step 3: PO Statuses' },
      { type: 'list', items: [
        'Draft — PO is being prepared, not yet sent to vendor',
        'Open — PO has been confirmed and sent to vendor, material is on order',
        'Closed — All items have been received, PO is complete',
        'Cancelled — PO has been voided',
      ]},
      { type: 'heading', value: 'Step 4: View PO Detail' },
      { type: 'text', value: 'Click any PO in the list to open the detail view. Here you can see all line items, amounts, and modify the PO if it is still in Draft or Open status.' },
      { type: 'tip', value: 'Open POs are factored into MRP calculations as "on order" supply. Keep PO statuses up to date for accurate MRP results.' },
    ],
  },
  {
    id: 'sales-orders',
    title: 'Sales Orders',
    content: [
      { type: 'text', value: 'Sales Orders (SOs) record confirmed customer orders and serve as real demand signals for your production and purchasing planning.' },
      { type: 'heading', value: 'Step 1: View Sales Orders' },
      { type: 'text', value: 'Navigate to Sales → Sales Orders from the sidebar. The list shows: SO Number, Customer, Posting Date, Delivery Date, Status, and Subtotal.' },
      { type: 'heading', value: 'Step 2: Create a New Sales Order' },
      { type: 'list', items: [
        'Click "+ Add New" to open the Sales Order form',
        'The SO Number is auto-generated (e.g., SO-2026-0001)',
        'Enter the Customer Name and Contact Person',
        'Set the Posting Date and required Delivery Date',
        'Choose the Currency (INR, USD, EUR, JPY, GBP) and apply Tax % if needed',
        'Add Freight Charges if applicable',
        'Click the "Contents" tab to add line items',
        'For each line: search for an Item, enter Quantity, Unit Price, and an optional Discount %',
        'Optionally set a Warehouse and Required Date per line',
        'Click "Save" to create the SO in Draft status',
      ]},
      { type: 'heading', value: 'Step 3: Sales Order Statuses' },
      { type: 'list', items: [
        'Draft — SO is being prepared, not yet confirmed',
        'Open — SO has been confirmed and is awaiting fulfillment',
        'Closed — All items have been shipped/delivered, SO is complete',
        'Cancelled — SO has been voided',
      ]},
      { type: 'heading', value: 'Step 4: Header Tab' },
      { type: 'text', value: 'The Header tab contains customer and financial details:' },
      { type: 'list', items: [
        'Customer Name and Contact Person',
        'Posting Date (document date) and Delivery Date',
        'Currency and Tax %',
        'Freight Charges and optional Remarks',
        'Status — update as the order progresses',
      ]},
      { type: 'heading', value: 'Step 5: Contents Tab (Line Items)' },
      { type: 'text', value: 'The Contents tab lists all ordered items. Use the item search box to find items by number or description. Totals are calculated automatically:' },
      { type: 'list', items: [
        'Subtotal — Sum of (Qty × Unit Price × (1 − Discount%))',
        'Tax Amount — Subtotal × Tax %',
        'Freight — Additional shipping charges',
        'Grand Total — Subtotal + Tax + Freight',
      ]},
      { type: 'tip', value: 'Use Open Sales Orders as the basis for production planning. Create a Production Order or Purchase Order to fulfil each SO line.' },
    ],
  },
  {
    id: 'production-orders',
    title: 'Production Orders',
    content: [
      { type: 'text', value: 'Production Orders are used to plan and track the manufacturing of finished goods. The form is designed to match SAP B1 — every field and tab mirrors the SAP Production Order document.' },

      { type: 'heading', value: 'Step 1: View the Production Orders List' },
      { type: 'text', value: 'Navigate to Production → Production Order from the sidebar. The list shows: Order No., Item No., Description, Planned Qty, Status, Due Date, and Origin. Use the search bar and status filter dropdown to find specific orders.' },

      { type: 'heading', value: 'Step 2: Open a New Production Order' },
      { type: 'list', items: [
        'Click the "+ Add" button (top-right of the list) to open a blank Production Order form',
        'The form opens in "new order" mode — all fields are editable',
        'The order number auto-generates (e.g., PROD-0222) but you can type a custom number',
      ]},

      { type: 'heading', value: 'Step 3: Header Fields — Left Column' },
      { type: 'list', items: [
        'No. — Two parts: Series dropdown (left half, selects the numbering series) and Order No. text box (right half, editable)',
        'Status — Select from: Planned, Released, In Progress, Completed, Cancelled',
        'Type — Production type: Standard, Special, or Disassembly',
        'Origin — How the order was created: Manual, Sales Order, or MRP',
        'Item — Type at least 2 characters to search items; click the result to select',
        'Planned Qty — Enter the quantity to produce (free text field, not restricted to numbers)',
        'UoM Name — Unit of Measure text field (e.g. PCS, KG, SET) shown next to Planned Qty',
        'Due Date — Target completion date for the production run',
        'Start Date — When manufacturing should begin',
        'Warehouse — Destination warehouse where the finished goods will be stored',
      ]},

      { type: 'heading', value: 'Step 4: Header Fields — Right Column' },
      { type: 'list', items: [
        'Customer — Customer name linked to this production run',
        'Sales Employee / User — Select from the dropdown list of active system users',
        'Distribution Rule — Cost distribution code (e.g., DEPT-PROD)',
        'Project No. — Internal project reference number',
        'Order Date — Document date (defaults to today)',
        'Routing Date Calc — Choose: On Start Date, On Due Date, Backward, or Forward',
        'Priority — Numeric priority (default 100; lower = higher priority)',
        'Procurement Items — Toggle whether sub-items need to be procured',
        'Linked To — Link type (Sales Order, Purchase Order)',
      ]},

      { type: 'heading', value: 'Step 5: Components Tab' },
      { type: 'text', value: 'The Components tab lists all raw materials and sub-components needed for this production order.' },
      { type: 'list', items: [
        'Click the "Components (N)" tab to view existing component lines',
        'Click "+ Add Component" to open the Add Component modal',
        'Select Line Type: Item (raw material) or Resource (labour/machine)',
        'Search and select the Item or Resource from the dropdown',
        'Enter Planned Qty, Unit Cost, and UoM',
        'Set the Issue Method: Manual (picked individually) or Backflush (auto-deducted on completion)',
        'Choose the Warehouse where this component is stored',
        'Click "Add" to save the component line',
        'Components can be added even on new (unsaved) orders — they are saved when you click Add/Update',
      ]},

      { type: 'heading', value: 'Step 6: Summary Tab' },
      { type: 'text', value: 'The Summary tab shows financial and operational details across three columns:' },
      { type: 'list', items: [
        'Costs column: Actual Item Component Cost, Actual Resource Component Cost, Actual Product Cost, Total Variance, and Journal Remark field',
        'Quantities + Dates column: Planned Qty (read-only), Completed Quantity (editable), Rejected Quantity (editable), Due Date (read-only), Actual Closing Date (editable), and Overdue indicator',
        'Planned Times + Days column: Total Production Time, Total Additional Time, Total Run Time, Required Days, Total Days',
        'Referenced Document "…" button — click to open the Reference Information dialog (see Step 8)',
      ]},

      { type: 'heading', value: 'Step 7: Attachments Tab' },
      { type: 'list', items: [
        'Click the "Attachments (N)" tab',
        'Click "+ Add Attachment" to open the attachment form',
        'Enter the File Name, Target Path, File Extension, Attached By, and optional Free Text note',
        'Click "Add" to save the attachment record',
        'All attachments appear in the table with their date and metadata',
      ]},

      { type: 'heading', value: 'Step 8: Referenced Document ("…" Button)' },
      { type: 'list', items: [
        'In the Summary tab, click the "…" button next to Referenced Document',
        'The Reference Information dialog opens (same as in Inventory Transfers)',
        'Tab 1 — "Document Referenced To": add references to Sales Orders, Purchase Orders, etc.',
        '  • Each row: Transaction Type, Doc. Number, Ext. Doc. Number, Date, Remarks',
        '  • Click "+ Add Row" to add a new reference line',
        'Tab 2 — "Document Referenced By": (read-only) shows documents that reference this Production Order',
        'Click "OK" to save references or "Cancel" to discard',
      ]},

      { type: 'heading', value: 'Step 9: Remarks & Pick and Pack' },
      { type: 'list', items: [
        'Below the tabs, two text areas are always visible:',
        'Remarks — Internal notes about this production order',
        'Pick and Pack Remarks — Instructions for the warehouse packing team',
        'Both fields are available on new and existing orders',
      ]},

      { type: 'heading', value: 'Step 10: Save / Update / Cancel' },
      { type: 'list', items: [
        'New order: click "Add" to create the production order',
        'Existing order: click "Update" to save changes',
        'Click "Cancel" to discard changes and return to the list',
        'Orders with status "Completed" or "Cancelled" are locked — fields become read-only',
      ]},

      { type: 'heading', value: 'Step 11: Production Order Statuses' },
      { type: 'list', items: [
        'Planned — Scheduled but not yet started; all fields editable',
        'Released — Materials allocated, production is authorized; still editable',
        'In Progress — Manufacturing actively underway; still editable',
        'Completed — All units produced; form is locked (read-only)',
        'Cancelled — Order voided; form is locked (read-only)',
      ]},

      { type: 'tip', value: 'Before creating a Production Order, ensure the item exists in Item Master Data with Procurement Method = "Make". For accurate cost tracking, set up the Bill of Materials (BOM) on the item first — component costs will auto-populate in the Summary tab.' },
    ],
  },
  {
    id: 'forecasts',
    title: 'Forecasts',
    content: [
      { type: 'text', value: 'Forecasts define the expected demand for items over future periods. They are the primary demand source for MRP calculations.' },
      { type: 'heading', value: 'Step 1: View Forecasts' },
      { type: 'text', value: 'Navigate to MRP → Forecasts from the sidebar. The table shows all forecast entries with Scenario Name, Item No., Description, Period, Quantity, and Created Date.' },
      { type: 'heading', value: 'Step 2: Create a Forecast Scenario' },
      { type: 'list', items: [
        'Click "New Scenario" to begin adding forecasts',
        'Enter a Scenario Name (e.g., "Q2 2026 Base")',
        'Select the Item you want to forecast',
        'Set the Period Date (the month/date when demand is expected)',
        'Enter the forecasted Quantity',
        'Repeat for all items you want to forecast',
        'Click "Save" to store the forecast entries',
      ]},
      { type: 'heading', value: 'Step 3: Understanding Forecast Impact' },
      { type: 'text', value: 'MRP reads the forecast quantity as demand. During an MRP run:' },
      { type: 'list', items: [
        'Total Demand = Sum of all forecast quantities for an item within the MRP date range',
        'Total Supply = Current stock (available in warehouses) + Open PO quantities',
        'Gap = Demand − Supply',
        'If Gap > 0, MRP generates an order recommendation',
      ]},
      { type: 'tip', value: 'Create forecasts before running MRP. Without forecasts, MRP will not detect any demand and will not generate recommendations.' },
    ],
  },
  {
    id: 'mrp-wizard',
    title: 'MRP Wizard',
    content: [
      { type: 'text', value: 'The MRP Wizard is the core engine that calculates material requirements and generates order recommendations.' },
      { type: 'heading', value: 'Step 1: Open the MRP Wizard' },
      { type: 'text', value: 'Navigate to MRP → MRP Wizard from the sidebar. You will see the MRP run form and a history of previous runs.' },
      { type: 'heading', value: 'Step 2: Configure the MRP Run' },
      { type: 'list', items: [
        'Enter a Run Name (e.g., "Q2 2026 Full MRP Run")',
        'Set the Start Date — beginning of the planning horizon',
        'Set the End Date — end of the planning horizon',
        'The system will analyze all forecasts within this date range',
      ]},
      { type: 'heading', value: 'Step 3: Execute the MRP Run' },
      { type: 'list', items: [
        'Click "Run MRP" to start the calculation',
        'The system processes all active items with Planning Method = "MRP"',
        'For each item it calculates: Demand (from forecasts), Supply (warehouse stock + open POs), Gap (demand − supply)',
        'If a gap exists, it generates a recommendation with calendar-aware lead times',
        'The run status changes from "Running" to "Completed"',
      ]},
      { type: 'heading', value: 'Step 4: MRP Calculation Logic' },
      { type: 'text', value: 'The MRP algorithm performs these steps:' },
      { type: 'list', items: [
        '1. Fetch all items with planning_method = "MRP" and status = "Active"',
        '2. Sum available stock from all warehouses for each item',
        '3. Aggregate demand from forecasts within the date range',
        '4. Add open PO quantities as incoming supply',
        '5. Calculate gap = demand − (available_stock + on_order_qty)',
        '6. If gap > 0: recommended quantity = MAX(gap, minimum_order_quantity)',
        '7. Document type = "Production Order" for Make items, "Purchase Order" for Buy items',
        '8. Required date is calculated by subtracting lead time (skipping weekends and holidays)',
      ]},
      { type: 'heading', value: 'Step 5: View Run History' },
      { type: 'text', value: 'Below the run form, the MRP Run History table shows all past runs with their status and the number of recommendations generated. Click a run to view its details.' },
      { type: 'tip', value: 'Run MRP regularly (e.g., weekly) to keep recommendations current. Changing forecasts, stock levels, or POs will affect results.' },
    ],
  },
  {
    id: 'order-recommendations',
    title: 'Order Recommendations',
    content: [
      { type: 'text', value: 'After running MRP, the system generates order recommendations. This page shows all planned actions needed to meet demand.' },
      { type: 'heading', value: 'Step 1: View Recommendations' },
      { type: 'text', value: 'Navigate to MRP → Order Recommendation from the sidebar. Select an MRP run from the dropdown to view its recommendations.' },
      { type: 'heading', value: 'Step 2: Understanding Recommendations' },
      { type: 'text', value: 'Each recommendation includes:' },
      { type: 'list', items: [
        'Item No. and Description — The item that needs to be ordered/produced',
        'Recommended Qty — How many units to order or produce',
        'Document Type — "Purchase Order" (for Buy items) or "Production Order" (for Make items)',
        'Required Date — The latest date by which the order must be placed (accounts for lead time, weekends, and holidays)',
        'Procurement Method — Buy or Make',
        'Status — Planned (not yet actioned) or Released (converted to an actual order)',
      ]},
      { type: 'heading', value: 'Step 3: Act on Recommendations' },
      { type: 'list', items: [
        'Review each recommendation to confirm quantities and dates',
        'For Purchase Order recommendations: create a new PO for the vendor',
        'For Production Order recommendations: create a new production order',
        'Update the recommendation status once the order has been created',
      ]},
      { type: 'tip', value: 'The Required Date accounts for lead time and non-working days. If today is past the required date, you should prioritize that order immediately.' },
    ],
  },
  {
    id: 'company-calendar',
    title: 'Company Calendar',
    content: [
      { type: 'text', value: 'The Company Calendar manages holidays and non-working days that affect MRP lead time calculations.' },
      { type: 'heading', value: 'Step 1: View Calendar' },
      { type: 'text', value: 'Navigate to MRP → Company Calendar from the sidebar. You will see a calendar view showing all holidays and non-working days marked in color.' },
      { type: 'heading', value: 'Step 2: Add a Holiday' },
      { type: 'list', items: [
        'Click on a date in the calendar or use the "Add Holiday" button',
        'Enter the Date of the holiday',
        'Provide a Description (e.g., "Labour Day")',
        'Select the Leave Type (Public Holiday, Company Holiday, etc.)',
        'Check "Recurring" if this holiday occurs every year on the same date',
        'Click "Save" to add the entry',
      ]},
      { type: 'heading', value: 'Step 3: How Calendar Affects MRP' },
      { type: 'text', value: 'When MRP calculates the Required Date for a recommendation:' },
      { type: 'list', items: [
        'It starts from the forecast demand date and subtracts the lead time in working days',
        'Weekends (Saturday and Sunday) are automatically skipped',
        'Any date marked in the Company Calendar as a holiday is also skipped',
        'Recurring holidays apply every year',
        'This ensures the Required Date always falls on a working day',
      ]},
      { type: 'tip', value: 'Keep the calendar up to date. Missing holidays may result in MRP calculating required dates that fall on non-working days, causing delays.' },
    ],
  },
  {
    id: 'bom',
    title: 'Bill of Materials (BOM)',
    content: [
      { type: 'text', value: 'A Bill of Materials defines the raw materials and components required to manufacture a finished good (Make item).' },
      { type: 'heading', value: 'Step 1: Access BOM' },
      { type: 'text', value: 'Open an item with Procurement Method = "Make" in the Item Master Detail page. Navigate to the "Bill of Materials" tab.' },
      { type: 'heading', value: 'Step 2: Create a BOM' },
      { type: 'list', items: [
        'Click "Add BOM" or "New BOM" on the Bill of Materials tab',
        'Set the BOM Type (Production or Template)',
        'Add component lines — each line specifies:',
        '  - Component Item: the raw material or sub-component',
        '  - Quantity: how many units of this component are needed per 1 unit of the parent item',
        '  - UOM: unit of measure for the component',
        '  - Warehouse: where the component is stored',
        '  - Issue Method: "Manual" (picked individually) or "Backflush" (auto-deducted on completion)',
        'Save the BOM',
      ]},
      { type: 'heading', value: 'Step 3: BOM Example' },
      { type: 'text', value: 'For the ARM Welder Model X200 (ITM-0003):' },
      { type: 'list', items: [
        '5 KG of Welding Wire ER70S-6 (ITM-0001) — Backflush',
        '2 PCS of Mild Steel Plate 6mm (ITM-0002) — Manual',
      ]},
      { type: 'text', value: 'This means to produce 1 unit of X200, the system needs 5 KG of welding wire and 2 steel plates.' },
      { type: 'tip', value: 'Multi-level BOMs are supported. A Make item can be a component in another Make item\'s BOM.' },
    ],
  },
  {
    id: 'workflow',
    title: 'Complete MRP Workflow',
    content: [
      { type: 'text', value: 'Follow this end-to-end workflow to get the most out of the MRP system:' },
      { type: 'heading', value: 'Phase 1: Master Data Setup' },
      { type: 'list', items: [
        '1. Create Vendors — Set up all suppliers in Purchasing → Vendors',
        '2. Create Items — Add all raw materials, components, and finished goods in Inventory → Item Master Data',
        '3. Set Procurement Method — Mark items as "Buy" (purchased) or "Make" (manufactured)',
        '4. Set Planning Parameters — Lead time, minimum order qty, reorder point for each item',
        '5. Create BOMs — For each "Make" item, define its Bill of Materials with component quantities',
        '6. Set Initial Stock — Enter warehouse stock levels for each item',
      ]},
      { type: 'heading', value: 'Phase 2: Calendar Setup' },
      { type: 'list', items: [
        '1. Add Holidays — Enter all public holidays and company non-working days',
        '2. Mark Recurring — Set recurring flag for holidays that repeat every year',
      ]},
      { type: 'heading', value: 'Phase 3: Demand Planning' },
      { type: 'list', items: [
        '1. Create Forecasts — Enter expected demand quantities for each item by period',
        '2. Use meaningful scenario names (e.g., "Q2 2026 Base") for tracking',
        '3. Review forecast quantities to ensure they reflect realistic demand',
      ]},
      { type: 'heading', value: 'Phase 4: MRP Execution' },
      { type: 'list', items: [
        '1. Open MRP Wizard — Navigate to MRP → MRP Wizard',
        '2. Set Date Range — Cover the planning horizon (e.g., April to June 2026)',
        '3. Run MRP — Click "Run MRP" and wait for completion',
        '4. Review Results — Check the number of recommendations generated',
      ]},
      { type: 'heading', value: 'Phase 5: Order Management' },
      { type: 'list', items: [
        '1. View Recommendations — Open MRP → Order Recommendation',
        '2. For "Purchase Order" recommendations: create a PO in Purchasing → Purchase Order',
        '3. For "Production Order" recommendations: create an order in Production → Production Order',
        '4. Update recommendation status once actioned',
        '5. Track customer demand via Sales → Sales Orders; Open SOs indicate confirmed demand',
        '6. Repeat the MRP run periodically to refresh recommendations',
      ]},
    ],
  },
  {
    id: 'itr',
    title: 'Inventory Transfer Request (ITR)',
    content: [
      { type: 'text', value: 'An Inventory Transfer Request (ITR) is a document used to request the movement of items between warehouses. Once approved, it can be copied into an Inventory Transfer (IT) to execute the actual stock movement.' },

      { type: 'heading', value: 'How to Create an ITR' },
      { type: 'list', items: [
        'Navigate to Inventory → Inventory Transfer Requests from the left sidebar',
        'Click "Add" (or the + button) to open a new blank ITR form',
        'Fill in the header fields: Series (default "Primary"), Posting Date, Due Date, Document Date',
        'Select a From Warehouse and To Warehouse using the dropdown selectors',
        'Optionally set Price List, Sales Employee, Journal Remarks, and Remarks',
      ]},

      { type: 'heading', value: 'Adding Line Items' },
      { type: 'list', items: [
        'Each row in the table represents one item to transfer',
        'Click "Item No." or "Item Description" to search and select an item',
        'Set the Quantity (defaults to 1.0000)',
        'UoM Code and UoM Name auto-fill from the item master',
        'Fill in the 5 additional columns as needed:',
      ]},
      { type: 'list', items: [
        '  • Product Group — Category of the item (e.g., "Welding", "Electrical")',
        '  • Period — Time period reference (e.g., "Q2-2026", "Apr-2026")',
        '  • Requester — Name of the person requesting the transfer',
        '  • Request — Internal request reference number (e.g., "REQ-001")',
        '  • Report — Report type associated with this line (e.g., "Monthly", "Weekly")',
      ]},
      { type: 'list', items: [
        'Click "+ Add Row" at the bottom to add more line items',
        'To remove a row, click the × button on that row',
      ]},

      { type: 'heading', value: 'Saving the ITR' },
      { type: 'list', items: [
        'Click "Add" in the toolbar to save the ITR',
        'The system assigns an auto-generated ITR number (e.g., ITR-00001)',
        'Status is set to "Open" upon creation',
      ]},

      { type: 'heading', value: 'Copy To → Inventory Transfer' },
      { type: 'list', items: [
        'Open an existing ITR and click "Copy To" in the toolbar',
        'Select "Inventory Transfer" from the dropdown',
        'A new IT form opens pre-filled with all header and line data from the ITR',
        'All 5 columns (Product Group, Period, Requester, Request, Report) are automatically copied',
        'Review the data, make any adjustments, and click "Add" to create the IT',
      ]},

      { type: 'heading', value: 'Reference Information Dialog' },
      { type: 'list', items: [
        'Click the "…" button next to the Referenced Document field to open the Reference Information dialog',
        'Tab 1 — "Document Referenced To": Add references to other documents (Sales Orders, Purchase Orders, etc.)',
        'Tab 2 — "Document Referenced By": View read-only list of documents that reference this ITR',
        'Click "Update" to save reference changes, or "Cancel" to discard',
      ]},

      { type: 'heading', value: 'Testing Steps — Create & Copy' },
      { type: 'list', items: [
        '1. Go to Inventory Transfer Requests list → click "Add"',
        '2. Select From Warehouse = "12 – NON MOVING" and To Warehouse = "10 – SCRAP MATERIAL"',
        '3. Add a line item: search for "A00001 – MS WIRE 0.8 MM 15 KG", set Qty = 5',
        '4. Fill in Product Group = "Welding", Period = "Q2-2026", Requester = your name',
        '5. Fill in Request = "REQ-001", Report = "Monthly"',
        '6. Set Remarks = "Test ITR" and click "Add" — confirm ITR number appears (e.g. ITR-00002)',
        '7. Now click "Copy To → Inventory Transfer"',
        '8. Verify all fields copy over, including the 5 line columns',
        '9. Click "Add" to create the IT — confirm IT number appears (e.g. IT-00004)',
        '10. Open the IT from the list and verify all line data matches the original ITR',
      ]},
    ],
  },
  {
    id: 'inventory-transfer',
    title: 'Inventory Transfer (IT)',
    content: [
      { type: 'text', value: 'An Inventory Transfer (IT) executes the actual movement of items between warehouses. It can be created standalone or by copying from an Inventory Transfer Request (ITR).' },

      { type: 'heading', value: 'How to Create a Standalone IT' },
      { type: 'list', items: [
        'Navigate to Inventory → Inventory Transfers from the left sidebar',
        'Click "Add" to open a new blank IT form',
        'Fill in the header fields: Series, Number (leave blank for auto or type a custom number), Posting Date, Document Date',
        'Select From Warehouse and To Warehouse',
        'Set Price List, Sales Employee, Journal Remarks, and Remarks as needed',
      ]},

      { type: 'heading', value: 'Adding Line Items' },
      { type: 'list', items: [
        'Same process as ITR — search and select items, set quantities',
        'Fill in the 5 additional columns: Product Group, Period, Requester, Request, Report',
        'Each line can have its own From/To warehouse override if different from the header',
      ]},

      { type: 'heading', value: 'Creating IT from ITR (Copy To)' },
      { type: 'list', items: [
        'Open an ITR document → click "Copy To → Inventory Transfer"',
        'All header fields (warehouses, dates, remarks) are pre-filled from the ITR',
        'All line items including Product Group, Period, Requester, Request, and Report are copied',
        'The Number field is blank (auto-assigned on save) — you can also type a custom number',
        'Review the pre-filled data, make any needed changes, then click "Add"',
      ]},

      { type: 'heading', value: 'IT Number Field' },
      { type: 'list', items: [
        'The Number field is editable — leave blank for auto-generated numbering (IT-00001, IT-00002, …)',
        'You can type a custom IT number if your workflow requires specific numbering',
      ]},

      { type: 'heading', value: 'Updating an IT' },
      { type: 'list', items: [
        'Open an existing IT from the list → make changes → click "Update"',
        'All fields including the 5 line columns can be modified',
        'The transfer number cannot be changed after creation',
      ]},

      { type: 'heading', value: 'Reference Information Dialog' },
      { type: 'list', items: [
        'Same as ITR — click "…" next to Referenced Document to open',
        'Use "Document Referenced To" tab to link this IT to other documents',
        '"Document Referenced By" tab shows documents referencing this IT',
      ]},

      { type: 'heading', value: 'Testing Steps — Standalone IT' },
      { type: 'list', items: [
        '1. Go to Inventory Transfers list → click "Add"',
        '2. Select From Warehouse = "12" and To Warehouse = "10"',
        '3. Add a line item: search for any item, set Qty',
        '4. Fill in Product Group, Period, Requester, Request, Report',
        '5. Set Remarks = "Standalone IT test" and click "Add"',
        '6. Verify IT number is generated and all line data is saved correctly',
        '7. Open the IT, update a field, click "Update", and confirm changes persist',
      ]},

      { type: 'heading', value: 'Line Columns Reference' },
      { type: 'list', items: [
        'Item No. — Item code from Item Master (e.g., A00001)',
        'Item Description — Full description of the item',
        'From Warehouse — Source warehouse for this line',
        'To Warehouse — Destination warehouse for this line',
        'Quantity — Number of units to transfer (4 decimal places)',
        'UoM Code — Unit of Measure code (e.g., KG, PC)',
        'UoM Name — Full UoM name (e.g., Kilogram, Piece)',
        'Product Group — Item category/group classification',
        'Period — Time period reference for the transfer',
        'Requester — Person who requested the transfer',
        'Request — Internal request reference number',
        'Report — Associated report type or identifier',
      ]},
      { type: 'heading', value: 'The source warehouse must actually hold the stock' },
      { type: 'text', value: 'When you post a transfer, the system checks the source warehouse first. If it does not hold enough, the transfer is refused and the message names the item, the warehouse, what is there and what was asked for.' },
      { type: 'note', value: 'This is deliberate. SAP refuses the same movement, and before this check a transfer appeared to work here and then quietly undid itself a few hours later — leaving the stock moved in neither system while the person who moved it believed the job was done.' },
      { type: 'steps', items: [
        'Read which item and warehouse are short.',
        'Check the real figure in Item Master or Inventory.',
        'Reduce the quantity, pick a different source warehouse, or save it as a draft until stock arrives.',
      ]},

      { type: 'heading', value: 'Checking it reached SAP' },
      { type: 'text', value: 'The transfers list has an "In SAP" column so you can see at a glance what SAP did with each one.' },
      { type: 'list', items: [
        'A number — SAP posted it, and that is SAP’s own document number',
        'Pending — queued, not posted there yet',
        'Refused (red) — SAP would not accept it. Hover to read why',
        'Blank — a draft or cancelled transfer, which is not meant to go to SAP',
      ]},
      { type: 'tip', value: 'A transfer moves stock here the moment you post it. If it never reaches SAP, the movement exists in neither system once stock is next refreshed — so it is worth a glance at this column after posting a batch.' },

    ],
  },
  {
    id: 'sap-overview',
    title: 'Working Alongside SAP',
    content: [
      { type: 'text', value: 'Some of the company works in SAP Business One and some works here. The two are kept in step automatically, in both directions. You do not have to copy anything by hand.' },
      { type: 'heading', value: 'What comes IN from SAP, and how quickly' },
      { type: 'list', items: [
        'Within seconds — a change made in SAP to an item, sales order, BOM, purchase order, production order or stock movement reaches this system through a live feed',
        'Through the day — scheduled checks re-read open purchase and production orders, so nothing is missed if the live feed skips a beat',
        'Overnight — a full stock and price reconciliation against SAP',
      ]},
      { type: 'heading', value: 'What goes OUT to SAP' },
      { type: 'list', items: [
        'Purchase orders — when you press Confirm Order. They arrive in SAP as a draft for approval there',
        'Inventory transfers — when you post or open them',
        'Production orders stay in this system and are not sent to SAP',
      ]},
      { type: 'note', value: 'Nothing is sent to SAP the moment you press the button. Documents join a queue and are sent one after another, usually within seconds. The next section shows you how to check.' },
      { type: 'tip', value: 'Stock figures you see here are SAP\u2019s figures. If a number looks wrong, it is nearly always because the stock genuinely moved in SAP, not because the two have drifted apart.' },
    ],
  },
  {
    id: 'sap-check',
    title: 'Checking a Document Reached SAP',
    content: [
      { type: 'text', value: 'Go to Settings → SAP Integration. Four tabs tell you everything about traffic between the two systems.' },
      { type: 'heading', value: 'The tabs' },
      { type: 'list', items: [
        'Configuration — the connection to SAP and which document types are allowed to go out',
        'Sync Logs — every sync, split into Inbound (SAP → here) and Outbound (here → SAP)',
        'PO Approvals — every purchase order sent to SAP, who sent it and what happened',
        'Inventory Transfers — the same for stock transfers',
      ]},
      { type: 'heading', value: 'Reading the Queue Status' },
      { type: 'list', items: [
        'Queued — waiting its turn. The Queued column shows how long it has been waiting',
        'Sending — being sent to SAP right now',
        'Sent — SAP accepted it. The SAP Doc column shows the number SAP gave it',
        'Failed — SAP refused it. The Error column says why, and there is a Retry button',
        'Skipped — deliberately not sent, for example a document dated before the integration was switched on',
      ]},
      { type: 'heading', value: 'To check one document' },
      { type: 'steps', items: [
        'Open Settings → SAP Integration.',
        'Choose the PO Approvals tab for a purchase order, or Inventory Transfers for a stock transfer.',
        'Find your document number in the first column. The list shows the most recent first.',
        'Look at Queue Status. If it says Sent, SAP has it, and SAP Doc shows SAP\u2019s own number.',
        'If it says Failed, read the Error column. If the problem is fixed, press Retry.',
        'Press "Open PO" or "Open transfer" to go back to the document itself.',
      ]},
      { type: 'heading', value: 'If the whole queue seems to have stopped' },
      { type: 'text', value: 'There is a limit on how many documents may be sent to SAP in an hour, so that a very large batch cannot overwhelm it. When the limit is reached the queue pauses and starts again by itself.' },
      { type: 'note', value: 'A yellow banner on the Outbound tab tells you when this has happened, how many documents are waiting, and roughly when sending will resume. Nothing is lost; it is only waiting.' },
      { type: 'tip', value: 'The transfers list has an "In SAP" column, so you can see at a glance which transfers reached SAP without opening this screen at all. Red "Refused" means SAP would not take it \u2014 hover for the reason.' },
    ],
  },
  {
    id: 'sap-live',
    title: 'SAP Live Updates Monitor',
    content: [
      { type: 'text', value: 'Settings → SAP Live Updates shows the live feed coming from SAP: what changed, when it arrived and whether it saved correctly.' },
      { type: 'list', items: [
        'Recent updates — each change SAP sent, what it was about and what was saved here',
        'By data type — how long since each kind of record was last heard from, judged against how often that kind normally changes',
        'Failed updates — a button in the bottom right corner. Grey with a green dot means nothing has failed; red with a count means something needs attention',
      ]},
      { type: 'heading', value: 'If something failed' },
      { type: 'steps', items: [
        'Press the red button in the bottom right corner.',
        'A panel slides in listing what failed, how many times it was tried and the reason.',
        'Send the reason to whoever supports the system \u2014 it names the exact problem.',
      ]},
      { type: 'note', value: 'Quiet is normal for some record types. A kind of document that only changes twice a week is not in trouble because it has been silent for a day; the screen judges each one against its own rhythm.' },
    ],
  },
  {
    id: 'numbering',
    title: 'Document Numbering',
    content: [
      { type: 'text', value: 'Purchase orders raised here use the same number range as SAP, so there is one sequence across both systems rather than two that clash.' },
      { type: 'list', items: [
        'When you raise a purchase order, the next free number is used',
        'A number already taken by a purchase order created in SAP is skipped automatically',
        'Super Admins can see and adjust the series under Settings → Numbering',
      ]},
      { type: 'heading', value: 'Why the SAP number can differ' },
      { type: 'text', value: 'A purchase order sent to SAP arrives as a draft awaiting approval. Until someone approves it in SAP, SAP has not given it a final number, and the screen says "Awaiting approval" rather than showing a number that may still change.' },
      { type: 'tip', value: 'Do not quote a number from a draft to a supplier until the PO Approvals tab shows it as Sent with a SAP Doc number against it.' },
    ],
  },
  {
    id: 'notifications',
    title: 'Notifications & The Daily Email',
    content: [
      { type: 'text', value: 'The bell in the top bar shows things needing attention. The number on it counts only items you can actually clear.' },
      { type: 'heading', value: 'Using the bell' },
      { type: 'steps', items: [
        'Press the bell to open the panel.',
        'Use the tabs \u2014 All, Production, Purchase orders, Vendors \u2014 to see one kind at a time.',
        'Press an item to expand it, or "Mark all read" to clear them.',
      ]},
      { type: 'note', value: 'Vendor notices are worked out fresh each time you look, so they are always current and cannot be marked read. They are shown but are not counted on the bell.' },
      { type: 'heading', value: 'The 9 am email' },
      { type: 'list', items: [
        'Every morning at 9 am a summary goes out covering production orders due or overdue, and overdue purchase orders',
        'It reports something once. An order that was reported yesterday is not repeated today unless its situation has changed \u2014 for example due soon becoming overdue',
        'Super Admins and Admins receive it automatically. Extra addresses can be added under Settings → Email Notifications',
      ]},
      { type: 'tip', value: 'If the email stops arriving, check Settings → Email Notifications first; the sending mailbox is shown there and can be tested from that screen.' },
    ],
  },
  {
    id: 'reports',
    title: 'Reports',
    content: [
      { type: 'text', value: 'Reports answer questions about what has already happened. They are read-only and safe to open at any time.' },
      { type: 'list', items: [
        'Inventory Transfer Report — every stock movement for a project, whether it was raised here or in SAP, with the project each movement was for',
        'New Procurement Items — items appearing in purchasing for the first time',
        'Inventory Ageing — how long stock has been sitting, by warehouse',
        'Customer Receivables Ageing and Vendor Liabilities Ageing — money owed to and by the company, grouped by how overdue it is',
        'Vendor Performance, Vendor Score and Vendor Risk — how suppliers are doing on delivery, quality and price',
      ]},
      { type: 'tip', value: 'If the Inventory Transfer Report looks empty for a project, check the project code on the transfers themselves \u2014 the report groups by that field, and a movement with no project code cannot appear under one.' },
    ],
  },
  {
    id: 'tips',
    title: 'Tips & Best Practices',
    content: [
      { type: 'heading', value: 'Data Accuracy' },
      { type: 'list', items: [
        'Keep stock levels accurate — update warehouse quantities after receiving goods',
        'Close completed POs — this removes them from "on order" supply calculations',
        'Update production order statuses promptly to reflect actual shop floor progress',
      ]},
      { type: 'heading', value: 'MRP Best Practices' },
      { type: 'list', items: [
        'Run MRP at least once per week to capture the latest changes',
        'Create forecasts before running MRP — no forecasts means no demand detected',
        'Review and act on recommendations promptly to avoid stockouts',
        'Set appropriate lead times for each item to ensure realistic Required Dates',
        'Use minimum order quantities to prevent impractical small orders',
      ]},
      { type: 'heading', value: 'Calendar Management' },
      { type: 'list', items: [
        'Add all holidays before the start of each year',
        'Use the recurring flag for holidays that repeat annually',
        'Remember: weekends are automatically treated as non-working days',
      ]},
      { type: 'heading', value: 'Keyboard Shortcuts' },
      { type: 'list', items: [
        'Use the search bars on each page to quickly find items, POs, or vendors',
        'Click column headers to sort tables',
        'Use the tab strip in the top bar for quick module switching',
      ]},
    ],
  },
  {
    id: 'troubleshooting',
    title: 'When Something Does Not Look Right',
    content: [
      { type: 'heading', value: 'A transfer will not save' },
      { type: 'text', value: 'If the source warehouse does not hold enough, the save is refused and the message names the item, the warehouse, what is there and what was asked for. This is deliberate: SAP would refuse the same movement, and previously the transfer appeared to work here and then quietly undid itself.' },
      { type: 'steps', items: [
        'Read which item and warehouse are short.',
        'Check the real stock in Item Master or Inventory.',
        'Either reduce the quantity, choose a different source warehouse, or save it as a draft until the stock arrives.',
      ]},
      { type: 'heading', value: 'A document has not appeared in SAP' },
      { type: 'steps', items: [
        'Open Settings → SAP Integration and find it on the PO Approvals or Inventory Transfers tab.',
        'Queued means it is waiting \u2014 give it a few minutes.',
        'Failed means SAP refused it; the Error column says why.',
        'If a yellow banner says the hourly limit is reached, it will resume by itself.',
      ]},
      { type: 'heading', value: 'A number looks different from SAP' },
      { type: 'text', value: 'Stock, prices and order quantities here are SAP\u2019s own figures, refreshed continuously. A difference almost always means the figure moved in SAP a moment ago rather than that the systems disagree. Settings → SAP Live Updates shows when that record last came through.' },
      { type: 'heading', value: 'The screen looks out of date' },
      { type: 'steps', items: [
        'Press View → Refresh, or F5.',
        'If it still looks wrong after an update has been released, hold Ctrl and press F5 to load the newest version.',
        'Settings → System Information shows which version you are running.',
      ]},
      { type: 'heading', value: 'Reporting a problem' },
      { type: 'list', items: [
        'Say which screen you were on and what you pressed',
        'Include the document number',
        'Include the exact message, or a photograph of it',
        'Include the Build shown in Settings → System Information',
      ]},
    ],
  },
];

/* ══════════ COMPONENT ══════════ */
export default function UserGuide() {
  const [activeSection, setActiveSection] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');

  const section = SECTIONS.find(s => s.id === activeSection) || SECTIONS[0];

  const filteredSections = searchTerm
    ? SECTIONS.filter(s =>
        s.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        JSON.stringify(s.content).toLowerCase().includes(searchTerm.toLowerCase())
      )
    : SECTIONS;

  return (
    <div className="flex flex-col lg:flex-row gap-4 lg:h-[calc(100vh-100px)]">
      {/* ── Left: Table of Contents ── */}
      <div className="w-full lg:w-56 shrink-0 card flex flex-col max-lg:max-h-72">
        <div className="px-3 py-2.5 border-b border-sap-border/60 bg-sap-blue/5">
          <span className="text-xs font-bold text-sap-text">📖 Table of Contents</span>
        </div>
        <div className="px-2 pt-2">
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search guide..."
            className="w-full px-2 py-1.5 text-xs border border-sap-border/60 rounded-lg focus:outline-none focus:ring-1 focus:ring-sap-blue/30"
          />
        </div>
        <nav className="flex-1 overflow-y-auto py-2 px-1">
          {filteredSections.map(s => (
            <button
              key={s.id}
              onClick={() => { setActiveSection(s.id); setSearchTerm(''); }}
              className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition-colors mb-0.5
                ${activeSection === s.id
                  ? 'bg-sap-blue/10 text-sap-blue font-semibold'
                  : 'text-sap-textLight hover:bg-sap-blue/5 hover:text-sap-text'}`}
            >
              {SECTIONS.indexOf(s) + 1}. {s.title}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Right: Content ── */}
      <div className="flex-1 card flex flex-col">
        <div className="px-5 py-3 border-b border-sap-border/60 bg-sap-blue/5 flex items-center justify-between">
          <span className="text-sm font-bold text-sap-text">
            {SECTIONS.indexOf(section) + 1}. {section.title}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const idx = SECTIONS.findIndex(s => s.id === activeSection);
                if (idx > 0) setActiveSection(SECTIONS[idx-1].id);
              }}
              disabled={SECTIONS.findIndex(s => s.id === activeSection) === 0}
              className="px-2 py-0.5 text-xs bg-sap-blue/10 text-sap-blue rounded-md hover:bg-sap-blue/20 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ← Prev
            </button>
            <button
              onClick={() => {
                const idx = SECTIONS.findIndex(s => s.id === activeSection);
                if (idx < SECTIONS.length - 1) setActiveSection(SECTIONS[idx+1].id);
              }}
              disabled={SECTIONS.findIndex(s => s.id === activeSection) === SECTIONS.length - 1}
              className="px-2 py-0.5 text-xs bg-sap-blue/10 text-sap-blue rounded-md hover:bg-sap-blue/20 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next →
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {section.content.map((block, i) => (
            <ContentBlock key={i} block={block} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Render content blocks ── */
function ContentBlock({ block }) {
  switch (block.type) {
    case 'text':
      return <p className="text-xs text-sap-text leading-relaxed">{block.value}</p>;
    case 'heading':
      return <h3 className="text-xs font-bold text-sap-blue mt-2 mb-1">{block.value}</h3>;
    case 'list':
      return (
        <ul className="space-y-1 ml-3">
          {block.items.map((item, i) => (
            <li key={i} className="text-xs text-sap-text leading-relaxed flex items-start gap-1.5">
              <span className="text-sap-blue mt-1 shrink-0">•</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case 'steps':
      return (
        <ol className="space-y-1.5 ml-1 mt-1">
          {block.items.map((item, i) => (
            <li key={i} className="text-xs text-sap-text leading-relaxed flex items-start gap-2">
              <span className="shrink-0 w-4 h-4 mt-0.5 rounded-full bg-sap-blue/10 text-sap-blue
                               text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
              <span>{item}</span>
            </li>
          ))}
        </ol>
      );
    case 'note':
      return (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mt-2">
          <span className="text-xs text-blue-900">
            <span className="font-bold">Note: </span>{block.value}
          </span>
        </div>
      );
    case 'tip':
      return (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
          <span className="text-xs text-amber-800">
            <span className="font-bold">💡 Tip: </span>{block.value}
          </span>
        </div>
      );
    default:
      return null;
  }
}
